import React, { useState, useEffect } from 'react';
import { partOrderService, vendorService } from '../../services/api';
import { toast } from 'react-toastify';
import { Tab } from '@headlessui/react';
import { Fragment } from 'react';
import { TableCellsIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import SupplyRequirementsPanel from './SupplyRequirementsPanel';
import FinancialForecastPanel from './FinancialForecastPanel';

const SupplyForecast = () => {
	const [requirements, setRequirements] = useState([]);
	const [financialForecast, setFinancialForecast] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [sortConfig, setSortConfig] = useState({
		                                             key: 'total_quantity_needed',
		                                             direction: 'descending'
	                                             });

	// For filtering
	const [searchTerm, setSearchTerm] = useState('');
	const [showCreateOrderDialog, setShowCreateOrderDialog] = useState(false);
	const [selectedVendor, setSelectedVendor] = useState(null);
	const [vendors, setVendors] = useState([]);
	const [orderSuccess, setOrderSuccess] = useState(false);
	const [isCreatingOrder, setIsCreatingOrder] = useState(false);

	// For forecast parameters
	const [forecastMonths, setForecastMonths] = useState(3);
	const [growthRate, setGrowthRate] = useState(10);
	const [lookbackMonths, setLookbackMonths] = useState(1);
	const [forecastMetadata, setForecastMetadata] = useState(null);
	const [showForecastSettings, setShowForecastSettings] = useState(false);
	const [selectedTab, setSelectedTab] = useState(0);

	useEffect(() => {
		fetchVendors();
	}, []);


	const fetchForecastedRequirements = async () => {
		try {
			setLoading(true);
			const actualForecastMonths = forecastMonths === '' ? 3 : forecastMonths;
			const actualGrowthRate = growthRate === '' ? 10 : growthRate;
			const actualLookbackMonths = lookbackMonths === '' ? 1 : lookbackMonths;

			// Fetch both requirements and financial forecast
			const [requirementsData, financialData] = await Promise.all([
				                                                            partOrderService.getForecastedSupplyOrder({
					                                                                                                      forecast_months: actualForecastMonths,
					                                                                                                      growth_rate: actualGrowthRate,
					                                                                                                      lookback_months: actualLookbackMonths
				                                                                                                      }),
				                                                            partOrderService.getProductFinancialForecast({
					                                                                                                         forecast_months: actualForecastMonths,
					                                                                                                         growth_rate: actualGrowthRate,
					                                                                                                         lookback_months: actualLookbackMonths
				                                                                                                         })
			                                                            ]);

			setRequirements(requirementsData);

			console.log('REQUIRMENTS DATA', requirementsData);
			console.log('Financial DATA', financialData);
			setFinancialForecast(financialData);
			if (requirementsData && requirementsData.length > 0 && requirementsData[0].forecast_metadata) {
				setForecastMetadata(requirementsData[0].forecast_metadata);
			}

			setError(null);
			setShowForecastSettings(false);
			toast.success("Forecast data loaded successfully");
		} catch (error) {
			console.error("Error fetching forecast data:", error);
			setError("Failed to load forecast data. Please try again.");
			toast.error("Error loading forecast data");
		} finally {
			setLoading(false);
		}
	};

	const fetchVendors = async () => {
		try {
			const vendorData = await vendorService.getVendors();
			setVendors(vendorData);
		} catch (error) {
			console.error("Error fetching vendors:", error);
			toast.error("Error loading vendors");
		}
	};

	const handleCreateOrder = async () => {
		if (!selectedVendor) {
			toast.error("Please select a vendor");
			return;
		}

		try {
			setIsCreatingOrder(true);
			const vendorRequirements = requirements.filter(
				req => req.vendor?.toLowerCase() === selectedVendor.toLowerCase()
			);

			if (vendorRequirements.length === 0) {
				toast.warning("No requirements found for the selected vendor");
				return;
			}

			const orderData = {
				vendor_id: vendors.find(v => v.name.toLowerCase() === selectedVendor.toLowerCase())?.id,
				items: vendorRequirements.map(req => ({
					part_type_id: req.part_type_id,
					quantity: req.units_to_order,
					unit_price: parseFloat(req.vendor_price) || 0
				}))
			};

			await partOrderService.createPartOrderFromRequirements(orderData);
			setOrderSuccess(true);
			setShowCreateOrderDialog(false);
			toast.success("Part order created successfully");
			fetchForecastedRequirements();
		} catch (error) {
			console.error("Error creating part order:", error);
			toast.error("Failed to create part order");
		} finally {
			setIsCreatingOrder(false);
		}
	};

	const handleForecastMonthsChange = (e) => {
		const inputValue = e.target.value;
		if (inputValue === '') {
			setForecastMonths('');
			return;
		}
		let value = parseInt(inputValue);
		if (!isNaN(value)) {
			if (value > 12) value = 12;
			if (value < 0) value = 0;
			setForecastMonths(value);
		}
	};

	const handleGrowthRateChange = (e) => {
		const inputValue = e.target.value;
		if (inputValue === '') {
			setGrowthRate('');
			return;
		}
		let value = parseInt(inputValue);
		if (!isNaN(value)) {
			if (value > 100) value = 100;
			if (value < 0) value = 0;
			setGrowthRate(value);
		}
	};

	const handleLookbackMonthsChange = (e) => {
		const inputValue = e.target.value;
		if (inputValue === '') {
			setLookbackMonths('');
			return;
		}
		let value = parseInt(inputValue);
		if (!isNaN(value)) {
			if (value > 12) value = 12;
			if (value < 0) value = 0;
			setLookbackMonths(value);
		}
	};

	return (
		<div className="container mx-auto px-4 py-6 max-w-[100%]">
			<div className="mb-6">
				<div className="flex flex-wrap justify-between items-center">
					<div>
						<h1 className="text-2xl font-bold text-gray-800">Supply Forecast</h1>
						<p className="mt-1 text-gray-600">Forecast future supply needs and financial metrics</p>
					</div>
					<div className="mt-4 sm:mt-0 flex items-center space-x-3">
						{requirements.length > 0 && (
							<button
								onClick={() => setShowCreateOrderDialog(true)}
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
								disabled={requirements.length === 0}
							>
								Create Supply Order
							</button>
						)}
						<button
							onClick={() => setShowForecastSettings(!showForecastSettings)}
							className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
						>
							{showForecastSettings ? 'Hide Forecast Settings' : 'Forecast Settings'}
						</button>
					</div>
				</div>
			</div>

			{showForecastSettings && (
				<div className="bg-white rounded-lg shadow mb-6 p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Forecast Settings</h3>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<div>
							<label htmlFor="forecastMonths" className="block text-sm font-medium text-gray-700 mb-1">
								Forecast Months (1-12)
							</label>
							<input
								type="number"
								id="forecastMonths"
								min="1"
								max="12"
								className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								value={forecastMonths}
								onChange={handleForecastMonthsChange}
							/>
							<p className="mt-1 text-sm text-gray-500">Number of months to forecast ahead</p>
						</div>
						<div>
							<label htmlFor="growthRate" className="block text-sm font-medium text-gray-700 mb-1">
								Growth Rate (0-100%)
							</label>
							<div className="mt-1 relative rounded-md shadow-sm">
								<input
									type="number"
									id="growthRate"
									min="0"
									max="100"
									className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-12"
									value={growthRate}
									onChange={handleGrowthRateChange}
								/>
								<div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
									<span className="text-gray-500 sm:text-sm">%</span>
								</div>
							</div>
							<p className="mt-1 text-sm text-gray-500">Projected monthly growth rate</p>
						</div>
						<div>
							<label htmlFor="lookbackMonths" className="block text-sm font-medium text-gray-700 mb-1">
								Lookback Months (1-12)
							</label>
							<input
								type="number"
								id="lookbackMonths"
								min="1"
								max="12"
								className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								value={lookbackMonths}
								onChange={handleLookbackMonthsChange}
							/>
							<p className="mt-1 text-sm text-gray-500">Months of historical data to consider</p>
						</div>
					</div>
					<div className="mt-4 flex justify-end">
						<button
							onClick={fetchForecastedRequirements}
							className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							disabled={loading}
						>
							{loading ? (
								<>
									<svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
									Generating Forecast...
								</>
							) : 'Generate Forecast'}
						</button>
					</div>
				</div>
			)}

			{forecastMetadata && (
				<div className="bg-white rounded-lg shadow mb-6 p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Forecast Information</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm text-gray-500">Forecast Period</p>
							<p className="text-xl font-semibold">{forecastMetadata.forecast_months} months</p>
						</div>
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm text-gray-500">Growth Rate</p>
							<p className="text-xl font-semibold">{forecastMetadata.growth_rate}%</p>
						</div>
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm text-gray-500">Historical Data</p>
							<p className="text-xl font-semibold">{forecastMetadata.lookback_months} months</p>
						</div>
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm text-gray-500">Forecast Date</p>
							<p className="text-xl font-semibold">{forecastMetadata.forecast_date}</p>
						</div>
					</div>
				</div>
			)}



			<div className="bg-white shadow rounded-lg overflow-hidden">
				<div className="p-5">
					<h2 className="text-lg font-medium text-gray-900">Forecast Details</h2>
				</div>
				<Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
					<Tab.List className="flex bg-gray-50 p-1 mx-5 mt-4 space-x-1 border-b border-gray-200 rounded-t-md">
						<Tab as={Fragment}>
							{({ selected }) => (
								<button
									className={`
                                        w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                        focus:outline-none focus:ring-0
                                        ${selected
									      ? 'bg-white text-primary-700 shadow'
									      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                    `}
								>
                                    <span className="flex items-center justify-center">
                                        <TableCellsIcon className="w-5 h-5 mr-2" />
                                        Supply Requirements
                                    </span>
								</button>
							)}
						</Tab>
						<Tab as={Fragment}>
							{({ selected }) => (
								<button
									className={`
                                        w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                        focus:outline-none focus:ring-0
                                        ${selected
									      ? 'bg-white text-primary-700 shadow'
									      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                    `}
								>
                                    <span className="flex items-center justify-center">
                                        <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                                        Financial Forecast
                                    </span>
								</button>
							)}
						</Tab>
					</Tab.List>
					<Tab.Panels>
						<Tab.Panel>
							<SupplyRequirementsPanel
								requirements={requirements}
								sortConfig={sortConfig}
								setSortConfig={setSortConfig}
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
								loading={loading}
								error={error}
								showForecastSettings={showForecastSettings}
								setShowForecastSettings={setShowForecastSettings}
							/>
						</Tab.Panel>
						<Tab.Panel>
							<FinancialForecastPanel
								financialForecast={financialForecast}
								loading={loading}
								error={error}
								showForecastSettings={showForecastSettings}
								setShowForecastSettings={setShowForecastSettings}
							/>
						</Tab.Panel>
					</Tab.Panels>
				</Tab.Group>
			</div>

			{showCreateOrderDialog && (
				<div className="fixed z-10 inset-0 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div className="fixed inset-0 transition-opacity" aria-hidden="true">
							<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
						</div>
						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Create Part Order Based on Forecast
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Select a vendor to create a part order for all forecasted parts from that vendor.
											</p>
										</div>
										<div className="mt-4">
											<label htmlFor="vendor" className="block text-sm font-medium text-gray-700">
												Vendor
											</label>
											<select
												id="vendor"
												name="vendor"
												className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
												value={selectedVendor || ''}
												onChange={(e) => setSelectedVendor(e.target.value)}
											>
												<option value="">Select a vendor</option>
												{[...new Set(requirements.map(req => req.vendor).filter(Boolean))].map((vendor) => (
													<option key={vendor} value={vendor}>
														{vendor}
													</option>
												))}
											</select>
										</div>
										{selectedVendor && (
											<div className="mt-4">
												<h4 className="text-sm font-medium text-gray-700">Parts to Order from {selectedVendor}</h4>
												<div className="mt-2 max-h-60 overflow-y-auto">
													<ul className="divide-y divide-gray-200">
														{requirements
															.filter(req => req.vendor === selectedVendor)
															.map((req) => (
																<li key={req.part_type_id} className="py-2">
																	<div className="flex justify-between">
																		<span className="text-sm font-medium">{req.part_type_name}</span>
																		<span className="text-sm text-gray-500">{req.units_to_order} units</span>
																	</div>
																	<div className="text-xs text-gray-500">
																		Estimated cost: ${parseFloat(req.estimated_cost || 0).toFixed(2)}
																	</div>
																</li>
															))}
													</ul>
												</div>
												<div className="mt-2 pt-2 border-t border-gray-200">
													<div className="flex justify-between font-medium">
														<span>Total:</span>
														<span>
                                                            ${requirements
															.filter(req => req.vendor === selectedVendor)
															.reduce((sum, req) => sum + parseFloat(req.estimated_cost || 0), 0)
															.toFixed(2)}
                                                        </span>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={handleCreateOrder}
									disabled={isCreatingOrder || !selectedVendor}
								>
									{isCreatingOrder ? 'Creating...' : 'Create Order'}
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => setShowCreateOrderDialog(false)}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{orderSuccess && (
				<div className="fixed bottom-0 right-0 m-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg">
					<div className="flex">
						<div className="py-1">
							<svg className="h-6 w-6 text-green-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<div>
							<p className="font-bold">Order Created</p>
							<p className="text-sm">Your part order has been created successfully based on the forecast.</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SupplyForecast;
